/**
 * Persona Gate Context
 * 
 * Provides a global way to require persona creation before performing actions.
 * Components call `requirePersona(action)` which either:
 * 1. Executes the action if persona exists
 * 2. Opens the creation modal and queues the action for after creation
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import PersonaCreationModal from '../components/PersonaCreationModal';

const PersonaGateContext = createContext(null);

export function PersonaGateProvider({ children }) {
    const { activePersona, isAuthenticated } = useAuth();
    const [modalOpen, setModalOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);

    /**
     * Require a persona before executing an action.
     * @param {Function} action - The action to execute (receives persona as first arg)
     * @returns {boolean} - True if action was executed, false if modal was opened
     */
    const requirePersona = useCallback((action) => {
        if (activePersona) {
            // Persona exists, execute action immediately
            action(activePersona);
            return true;
        } else {
            // No persona, open modal and queue action
            setPendingAction(() => action);
            setModalOpen(true);
            return false;
        }
    }, [activePersona]);

    /**
     * Check if user has a persona without requiring one
     */
    const hasPersona = !!activePersona;

    const handleModalClose = () => {
        setModalOpen(false);
        setPendingAction(null);
    };

    const handleSuccess = (newPersona, action) => {
        // Execute the pending action with the new persona
        if (action) {
            action(newPersona);
        }
        setPendingAction(null);
    };

    const value = {
        requirePersona,
        hasPersona,
        openPersonaModal: () => setModalOpen(true)
    };

    return (
        <PersonaGateContext.Provider value={value}>
            {children}
            <PersonaCreationModal
                isOpen={modalOpen}
                onClose={handleModalClose}
                onSuccess={handleSuccess}
                pendingAction={pendingAction}
            />
        </PersonaGateContext.Provider>
    );
}

export function usePersonaGate() {
    const context = useContext(PersonaGateContext);
    if (!context) {
        throw new Error('usePersonaGate must be used within a PersonaGateProvider');
    }
    return context;
}

export default PersonaGateContext;
